import React, { useState, useRef, useEffect } from 'react';
import { Image, Spin } from 'antd';
import { ImageProps } from 'antd/es/image';

interface ProgressiveImageProps extends Omit<ImageProps, 'src'> {
  thumbnailSrc?: string;
  fullSrc?: string;
  placeholderHeight?: number;
  preview?: boolean | ImageProps['preview'];
}

export const ProgressiveImage: React.FC<ProgressiveImageProps> = ({
  thumbnailSrc,
  fullSrc,
  placeholderHeight = 150,
  style,
  preview = true,
  ...rest
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Use thumbnail if available and no error, otherwise use full image
  const displaySrc = (thumbnailSrc && !imageError) ? thumbnailSrc : fullSrc;

  // Reset states when src changes
  useEffect(() => {
    if (displaySrc && imgRef.current?.src !== displaySrc) {
      setImageLoaded(false);
    }
  }, [displaySrc]);

  // Determine if preview should be enabled
  const previewEnabled = preview && fullSrc && imageLoaded;

  if (!displaySrc) {
    return (
      <div style={{ 
        width: '100%', 
        height: placeholderHeight, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: '#f0f0f0'
      }}>
        <Spin />
      </div>
    );
  }

  return (
    <>
      <img
        {...rest}
        ref={imgRef}
        src={displaySrc}
        style={{
          ...style,
          opacity: imageLoaded ? 1 : 0,
          transition: 'opacity 0.3s ease-in-out',
          cursor: previewEnabled ? 'pointer' : 'default'
        }}
        onLoad={() => setImageLoaded(true)}
        onError={() => {
          if (thumbnailSrc && !imageError && fullSrc) {
            // If thumbnail fails, fallback to full image
            setImageError(true);
          }
        }}
        onClick={() => {
          if (previewEnabled) {
            setShowPreview(true);
          }
        }}
        alt=''
      />
      {!imageLoaded && (
        <div style={{ 
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%', 
          height: placeholderHeight, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#f0f0f0'
        }}>
          <Spin />
        </div>
      )}
      {showPreview && fullSrc && (
        <Image
          style={{ display: 'none' }}
          src={fullSrc}
          preview={{
            visible: showPreview,
            src: fullSrc,
            onVisibleChange: (visible) => setShowPreview(visible)
          }}
        />
      )}
    </>
  );
};